import { useApiRequest } from "@kamae-apps/shared/Hooks/useApiRequest"
import { Module } from "@kamae-apps/shared/Types/Module"
import { OnboardingState } from "@kamae-apps/shared/Types/Onboarding"
import { useToast } from "@kamae-apps/shared/old/Component/Toast/Context"
import { errorToast, successToast } from "@kamae-apps/shared/old/Component/Toast/ToastBuilder"
import { apiRequest, dateToISODate } from "@kamae-apps/shared/utils"
import { useCallback, useEffect, useState } from "react"
import { AddELearningsToCompany, CompanyElearningAdmin, ElearningAdmin } from "../../../../../Types/ELearning"
import { CompanyData } from "../../../Companies"

export const useElearningContainer = (company: CompanyData, setCompany: (value: CompanyData) => void) => {
  const toast = useToast()
  const companyModules = useApiRequest<Module[]>(`/module/${company.id}`)
  const elearnings = useApiRequest<ElearningAdmin[]>(`/elearning/all/company/${company.id}`)
  const companyElearning = useApiRequest<CompanyElearningAdmin[]>(`/elearning/company/${company.id}`)
  const onboarding = useApiRequest<OnboardingState>(`/company/${company.id}/onboarding`)
  const [check, setCheck] = useState<boolean[]>([])
  const [monthDelta, setMonthDelta] = useState<number[]>([])
  const [loading, setLoading] = useState(false)
  const [allChecked, setAllChecked] = useState(false)

  const referenceDate: { date: Date; source: "onboarding" | "licence" } =
    onboarding?.onboarding_closed_at
      ? { date: new Date(onboarding.onboarding_closed_at), source: "onboarding" }
      : { date: new Date(company.license_start_date), source: "licence" }

  const changeCheckAll = useCallback(() => {
    setAllChecked(prev => {
      setCheck(prevChecked => prevChecked.map(() => !prev))
      return !prev
    })
  }, [])

  const updateCalendarType = useCallback(
    (isEnabled: boolean) => {
      const updated = { ...company, user_centric_calendar: isEnabled }
      setCompany(updated)
      apiRequest("/company/" + company.id, {
        method: "PUT",
        body: {
          name: company.name,
          idp: company.idp,
          login_method: company.loginMethod,
          status: company.status,
          license_start_date: dateToISODate(company.license_start_date),
          license_end_date: dateToISODate(company.license_end_date),
          max_users: company.max_users,
          max_admins: company.max_admins,
          phishing_enabled: company.phishing_enabled,
          sso_enabled: company.sso_enabled,
          lms_enabled: company.lms_enabled,
          newCompanyModules: [],
          activeCompanyModules: companyModules?.map(m => m.id) ?? [],
          defaultLanguageId: company.defaultLanguageId,
          dir_sync_enabled: company.dir_sync_enabled,
          automated_email_enabled: company.automated_email_enabled,
          external_customer_id: company.external_customer_id,
          teams_managed_by: company.teams_managed_by,
          user_centric_calendar: isEnabled,
        },
      })
        .then(() => toast.addToast(successToast("Calendrier mis à jour")))
        .catch(() => {
          setCompany({ ...company, user_centric_calendar: !isEnabled })
          toast.addToast(errorToast())
        })
    },
    [company, companyModules, setCompany, toast]
  )

  const saveElearnings = useCallback(() => {
    setLoading(true)
    const companyElearningId = companyElearning?.map(v => v.id) ?? []
    const addData: AddELearningsToCompany[] = check
      .map((v, i) => ({
        elearningId: v ? elearnings!![i].id : -1,
        monthDelta: monthDelta[i],
      }))
      .filter(v => v.elearningId !== -1 && !companyElearningId.includes(v.elearningId))
    apiRequest("/elearning/company/" + company.id, {
      method: "POST",
      body: addData,
    })
      .then(() => {
        elearnings
          ?.filter((v, i) => !check[i] && companyElearningId.includes(v.id))
          .forEach(v => {
            apiRequest("/elearning/" + v.id + "/company/" + company.id, {
              method: "DELETE",
            })
          })
        elearnings?.forEach((v, i) => {
          if (check[i] && companyElearningId.includes(v.id)) {
            apiRequest("/elearning/" + v.id + "/company/" + company.id, {
              method: "PUT",
              body: { monthDelta: monthDelta[i] },
            })
          }
        })
        toast.addToast(successToast("Elearning mis à jour"))
        setLoading(false)
      })
      .catch(err => {
        console.error(err)
        toast.addToast(errorToast())
        setLoading(false)
      })
  }, [check, company.id, companyElearning, monthDelta, elearnings, toast])

  useEffect(() => {
    const companyElearningId = companyElearning?.map(v => v.id) ?? []
    if (elearnings !== undefined) {
      setCheck(elearnings.map(v => companyElearningId.includes(v.id)))
      setMonthDelta(
        elearnings.map(v => {
          const companyEl = companyElearning?.find(e => e.id === v.id)
          return companyEl?.monthDelta ?? v.monthDelta
        })
      )
    }
  }, [companyElearning, elearnings])

  useEffect(() => {
    setAllChecked(check.every(v => v) && check.length > 0)
  }, [check])

  return {
    loading,
    allChecked,
    elearnings,
    check,
    setCheck,
    monthDelta,
    setMonthDelta,
    companyModules,
    changeCheckAll,
    saveElearnings,
    updateCalendarType,
    referenceDate,
  }
}
